import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { loadBase, loadPlayerStats, loadAdvancedStats } from '../../services/espnService';
import { useConfig } from '../../hooks/useConfig';

// ── Formula definitions per position ─────────────────────────────────────────
// Prefixes:
//   (none) = per-game stat, higher is better
//   "~"    = per-game stat, lower is better (penalty)
//   "="    = raw season stat (not divided by GP), higher is better
//   "=~"   = raw season stat, lower is better (penalty)
const OFF_FORMULAS = {
  QB:  ['=completionPct', 'passingYards', 'passingTouchdowns', '=~interceptionPct', '~sacks', 'rushingYards', 'rushingTouchdowns',
        '=passing_epa', '=cpoe', '=~pressure_pct'],
  RB:  ['rushingYards', '=yardsPerRushAttempt', 'rushingTouchdowns', 'receivingYards', '=yardsPerReception', 'receivingTouchdowns', '~fumbles',
        '=rushing_epa', '=ybc_att', '=rush_yards_over_expected_per_att'],
  WR:  ['receptions', 'receivingYards', 'receivingTouchdowns', 'receivingFirstDowns', 'rushingYards', 'rushingTouchdowns', '~fumbles',
        '=receiving_epa', '=avg_separation', '=~drop_percent'],
  TE:  ['receptions', 'receivingYards', 'receivingTouchdowns', 'receivingFirstDowns', 'rushingYards', 'rushingTouchdowns', '~fumbles',
        '=receiving_epa', '=avg_separation', '=~drop_percent'],
};

const OFF_POS_CAPS = { QB: 32, RB: 32, WR: 96, TE: 32 };

const MIN_GAMES = { QB: 8, RB: 6, WR: 6, TE: 6 };

const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const STAT_LABELS = {
  '=completionPct':       'Comp%',
  passingYards:           'Pass Yds/G',
  passingTouchdowns:      'Pass TD/G',
  '=~interceptionPct':    'INT% ↓',
  '~sacks':               'Sacks/G ↓',
  rushingYards:           'Rush Yds/G',
  rushingTouchdowns:      'Rush TD/G',
  '=yardsPerRushAttempt': 'Yds/Carry',
  '=yardsPerReception':   'Yds/Rec',
  receivingYards:         'Rec Yds/G',
  receivingTouchdowns:    'Rec TD/G',
  receivingFirstDowns:    'Rec 1D/G',
  receptions:             'Rec/G',
  receivingTargets:       'Tgt/G',
  '~fumblesLost':         'FL/G ↓',
  '~fumbles':             'Fum/G ↓',
  // Advanced stats
  '=passing_epa':                     'Pass EPA',
  '=cpoe':                            'CPOE',
  '=~pressure_pct':                   'Pressure% ↓',
  '=rushing_epa':                     'Rush EPA',
  '=ybc_att':                         'YBC/Att',
  '=rush_yards_over_expected_per_att':'RYOE/Att',
  '=receiving_epa':                   'Rec EPA',
  '=avg_separation':                  'Separation',
  '=~drop_percent':                   'Drop% ↓',
};

// ── Stat key parsing helpers ──────────────────────────────────────────────────
// Key format: ["=" raw] ["~" penalty] statName
// "="  → use season total as-is (not divided by GP)
// "~"  → lower is better
function parseKey(key) {
  let k = key;
  const isRaw     = k.startsWith('=');  if (isRaw)     k = k.slice(1);
  const isPenalty = k.startsWith('~');  if (isPenalty) k = k.slice(1);
  return { rawKey: k, isRaw, isPenalty };
}

// ── NR computation ────────────────────────────────────────────────────────────
function computeOffNR(pos, players, stats, weights = {}) {
  const formula = OFF_FORMULAS[pos];
  const cap = OFF_POS_CAPS[pos] ?? 65;
  const minGames = MIN_GAMES[pos] ?? 6;

  const eligible = players.filter(p => {
    const s = stats[p.id];
    return s && s.gamesPlayed >= minGames;
  }).map(p => {
    const s = stats[p.id];
    const gp = s.gamesPlayed;
    const perGame = {};
    formula.forEach(key => {
      const { rawKey, isRaw } = parseKey(key);
      const val = s[rawKey] ?? 0;
      // Raw stats are used as-is; per-game stats are divided by GP
      perGame[key] = isRaw ? val : (gp > 0 ? val / gp : 0);
    });
    return { ...p, perGame, gamesPlayed: gp };
  });

  if (!eligible.length) return [];

  // Rank each stat (ascending for penalty stats, descending for positive stats)
  const statRanks = {};
  eligible.forEach(p => { statRanks[p.id] = {}; });

  formula.forEach(key => {
    const { isPenalty } = parseKey(key);
    const sorted = [...eligible].sort((a, b) =>
      isPenalty
        ? a.perGame[key] - b.perGame[key]   // ascending — lower is better
        : b.perGame[key] - a.perGame[key]   // descending — higher is better
    );
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[key];
      let j = i;
      while (j < sorted.length && sorted[j].perGame[key] === tieVal) {
        statRanks[sorted[j].id][key] = rank;
        j++;
      }
      rank += j - i;
      i = j;
    }
  });

  // Sum weighted ranks → sort ascending → assign NR
  const withNR = eligible.map(p => {
    const rankSum = formula.reduce((acc, k) => {
      const w = weights[k] ?? 1;
      return acc + (statRanks[p.id][k] ?? eligible.length) * w;
    }, 0);
    return { ...p, rankSum };
  }).sort((a, b) => a.rankSum - b.rankSum);

  const result = [];
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum;
    let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) {
      result.push({ ...withNR[j], nr });
      j++;
    }
    nr += j - i;
    i = j;
  }

  // Non-qualifiers at cap
  const eligibleIds = new Set(result.map(p => p.id));
  players.forEach(p => {
    if (!eligibleIds.has(p.id)) {
      result.push({ ...p, nr: cap, perGame: {}, gamesPlayed: stats[p.id]?.gamesPlayed ?? 0 });
    }
  });

  return result;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const cellSx = {
  color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.75, px: 1,
};
const headerCellSx = {
  color: '#f5a623', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)',
  py: 0.75, px: 1, backgroundColor: '#060e1a',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function OffensiveNR() {
  const { config } = useConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [players, setPlayers] = useState({});
  const [stats, setStats] = useState({});
  const [posTab, setPosTab] = useState(0);

  useEffect(() => {
    setLoading(true);
    loadBase()
      .then(({ players: p }) => {
        const offPlayers = {};
        Object.values(p).forEach(pl => {
          if (['QB', 'RB', 'WR', 'TE'].includes(pl.posKey)) {
            offPlayers[pl.id] = pl;
          }
        });
        setPlayers(offPlayers);
        return Promise.all([
          loadPlayerStats(Object.keys(offPlayers)),
          loadAdvancedStats(),
        ]);
      })
      .then(([s, adv]) => {
        // Merge advanced stats into the stats map (additive — existing stats preserved)
        const merged = { ...s };
        Object.keys(merged).forEach(id => {
          if (adv[id]) merged[id] = { ...merged[id], ...adv[id] };
        });
        setStats(merged);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load offensive player data.'); setLoading(false); });
  }, []);

  const currentPos = POSITIONS[posTab];
  const formula = OFF_FORMULAS[currentPos] || [];

  const ranked = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(stats).length) return [];
    const posPlayers = Object.values(players).filter(p => p.posKey === currentPos);
    const weights = config[`nr_weights_off_${currentPos.toLowerCase()}`] ?? {};
    return computeOffNR(currentPos, posPlayers, stats, weights);
  }, [players, stats, currentPos, config]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#2ea84b' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Loading offensive stats…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  const qualifiers = ranked.filter(p => p.perGame && Object.keys(p.perGame).length > 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Offensive Nerd Rating
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2, lineHeight: 1.6 }}>
        Equal-weight rank-sum across key stats per game. ↓ stats are penalties — lower is better.
        Min {MIN_GAMES[currentPos]} games played to qualify.
      </Typography>

      <Tabs
        value={posTab}
        onChange={(_, v) => setPosTab(v)}
        textColor="inherit"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          borderBottom: '1px solid rgba(245,166,35,0.15)',
          '& .MuiTab-root': {
            minHeight: 36, fontWeight: 700, textTransform: 'none',
            fontSize: '0.82rem', px: 2, color: '#9eb8d0',
          },
          '& .Mui-selected': { color: '#f5a623' },
        }}
      >
        {POSITIONS.map(pos => (
          <Tab key={pos} label={pos} />
        ))}
      </Tabs>

      {qualifiers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#6a8aa8' }}>
            No qualifying {currentPos}s found (min {MIN_GAMES[currentPos]} games).
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 48 }}>NR</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 56 }}>Team</TableCell>
                <TableCell align="center" sx={{ ...headerCellSx, width: 36 }}>GP</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>Name</TableCell>
                {formula.map(k => (
                  <TableCell key={k} align="right" sx={{
                    ...headerCellSx,
                    color: parseKey(k).isPenalty ? '#ef4444' : '#f5a623',
                  }}>
                    {STAT_LABELS[k] ?? k}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {qualifiers.map((p, idx) => (
                <TableRow key={p.id} sx={{
                  backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                  '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
                }}>
                  <TableCell sx={{ ...cellSx, fontWeight: 900, color: p.nr <= 5 ? '#f5a623' : '#e8f0f8', fontSize: '0.82rem', width: 48 }}>
                    #{p.nr}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 56 }}>
                    <Chip label={p.team} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.25)' }} />
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellSx, width: 36 }}>{p.gamesPlayed}</TableCell>
                  <TableCell sx={{ ...cellSx, color: '#e8f0f8', fontWeight: 600 }}>{p.name}</TableCell>
                  {formula.map(k => (
                    <TableCell key={k} align="right" sx={{
                      ...cellSx,
                      color: parseKey(k).isPenalty ? 'rgba(239,68,68,0.8)' : '#9eb8d0',
                    }}>
                      {p.perGame[k] != null ? p.perGame[k].toFixed(2) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
